"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AcceptInviteRequest,
  CreateInviteRequest,
  CreateSlotRequest,
  InterviewCalendarQuery,
  RequestCancellationRequest,
} from "@chotca/sdk";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

/** Employer interview calendar ("Lịch phỏng vấn"). */
export function useEmployerInterviews(query: InterviewCalendarQuery = {}) {
  return useQuery({
    queryKey: qk.invites.interviews(query),
    queryFn: () => getSdk().invites.interviews(query),
    staleTime: 15_000,
  });
}

/** Employer reports a worker no-show on a committed interview. */
export function useReportNoShow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => getSdk().invites.reportNoShow(inviteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.invites.interviews() });
    },
  });
}

/** Worker blocks an org from sourcing/inviting them ("Ẩn quán này"). */
export function useBlockOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orgId: string) => getSdk().invites.blockOrg(orgId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.invites.mine });
    },
  });
}

export function useMyInvites(enabled = true) {
  return useQuery({
    queryKey: qk.invites.mine,
    queryFn: () => getSdk().invites.mine(),
    enabled,
  });
}

export function useMyInvite(inviteId: string | undefined) {
  return useQuery({
    queryKey: qk.invites.detail(inviteId ?? ""),
    queryFn: () => getSdk().invites.detail(inviteId!),
    enabled: !!inviteId,
  });
}

export function useAcceptInvite(inviteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AcceptInviteRequest) =>
      getSdk().invites.accept(inviteId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.invites.mine });
      qc.invalidateQueries({ queryKey: qk.invites.detail(inviteId) });
    },
  });
}

export function useRejectInvite(inviteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => getSdk().invites.reject(inviteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.invites.mine });
      qc.invalidateQueries({ queryKey: qk.invites.detail(inviteId) });
    },
  });
}

export function useRescheduleInvite(inviteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AcceptInviteRequest) =>
      getSdk().invites.reschedule(inviteId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.invites.mine });
      qc.invalidateQueries({ queryKey: qk.invites.detail(inviteId) });
    },
  });
}

export function useCancelInviteInWindow(inviteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => getSdk().invites.cancelInWindow(inviteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.invites.mine });
      qc.invalidateQueries({ queryKey: qk.invites.detail(inviteId) });
    },
  });
}

export function useRequestInviteCancellation(inviteId: string) {
  return useMutation({
    mutationFn: (body: RequestCancellationRequest) =>
      getSdk().invites.requestCancellation(inviteId, body),
  });
}

export function useOrgInvites(orgId: string, enabled = true) {
  return useQuery({
    queryKey: qk.invites.byOrg(orgId),
    queryFn: () => getSdk().invites.byOrg(orgId),
    enabled: enabled && !!orgId,
  });
}

export function useCreateInvite(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInviteRequest) =>
      getSdk().invites.create(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.invites.byOrg(orgId) });
      qc.invalidateQueries({ queryKey: qk.orgs.detail(orgId) });
      // Inviting spends a sourcing use → refresh the plan status the UI reads
      // its "Lượt mời phỏng vấn" count from, so it updates without a reload.
      qc.invalidateQueries({ queryKey: qk.orgs.planStatus(orgId) });
      qc.invalidateQueries({ queryKey: qk.applications.creditLedger });
      qc.invalidateQueries({ queryKey: qk.applications.employer() });
      qc.invalidateQueries({ queryKey: qk.applications.employerCounts() });
    },
  });
}

export function useSlotsForLocation(orgId: string, locationId: string) {
  return useQuery({
    queryKey: qk.slots.byLocation(orgId, locationId),
    queryFn: () => getSdk().invites.listSlotsByLocation(orgId, locationId),
    enabled: !!orgId && !!locationId,
  });
}

export function useCreateSlot(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSlotRequest) =>
      getSdk().invites.createSlot(orgId, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: qk.slots.byLocation(orgId, vars.orgAddressId),
      });
    },
  });
}
